import { useEffect, useRef } from 'react';
import { Platform, PermissionsAndroid } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const PERMISSION_ASKED_KEY = 'notificationPermissionAsked';

/**
 * Requests push-notification permission once on first app launch (native only).
 * - Android 13+ (API 33+): uses PermissionsAndroid to trigger the native
 *   POST_NOTIFICATIONS runtime permission dialog directly.
 * - Android < 13: notifications are auto-granted, no dialog needed.
 * - iOS: uses @react-native-firebase/messaging requestPermission().
 */
export function useNotificationPermission() {
  const requested = useRef(false);

  useEffect(() => {
    if (Platform.OS === 'web' || requested.current) return;
    requested.current = true;

    // Delay permission request by 2 seconds to ensure app is fully loaded
    const timeoutId = setTimeout(async () => {
      try {
        console.log('📱 Checking notification permission status...');
        console.log('📱 Platform:', Platform.OS, 'API Level:', Platform.Version);
        
        const alreadyAsked = await AsyncStorage.getItem(PERMISSION_ASKED_KEY);
        console.log('📱 Permission already asked?', alreadyAsked);
        
        if (alreadyAsked === 'true') {
          console.log('📱 Notification permission already requested previously');
          return;
        }

        if (Platform.OS === 'android') {
          // Android 13+ (API 33+) requires the POST_NOTIFICATIONS runtime permission.
          // PermissionsAndroid.request() shows the native system dialog directly.
          if ((Platform.Version as number) >= 33) {
            console.log('📱 Android 13+ detected, requesting POST_NOTIFICATIONS permission...');
            
            const result = await PermissionsAndroid.request(
              PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS,
              {
                title: 'Enable Notifications',
                message: 'Stay updated with new job opportunities and application updates',
                buttonPositive: 'Allow',
                buttonNegative: 'Not Now',
              }
            );
            
            console.log('📱 Permission result:', result);
            console.log(
              result === PermissionsAndroid.RESULTS.GRANTED
                ? '✅ Notification permission granted'
                : result === PermissionsAndroid.RESULTS.DENIED
                ? '❌ Notification permission denied'
                : '⚠️ Notification permission never_ask_again',
            );
          } else {
            console.log('📱 Android < 13, notifications auto-granted');
          }
        } else if (Platform.OS === 'ios') {
          // iOS requires going through the Firebase Messaging SDK.
          console.log('📱 iOS detected, requesting permission via Firebase...');
          const { default: messaging } = await import('@react-native-firebase/messaging');
          const authStatus = await messaging().requestPermission();
          const granted =
            authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
            authStatus === messaging.AuthorizationStatus.PROVISIONAL;
          console.log(granted ? '✅ Notification permission granted' : '❌ Notification permission denied');
        }

        await AsyncStorage.setItem(PERMISSION_ASKED_KEY, 'true');
        console.log('📱 Saved permission asked flag to AsyncStorage');
      } catch (error) {
        console.error('❌ Failed to request notification permission:', error);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, []);
}
